import { Menu, MenuItem, Divider, Typography } from "@mui/material";
import React, {
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { StyledContextMenu } from "./ContextMenu.styled";
import {
  CameraPlusIcon,
  CheckIcon,
  DownloadSimpleIcon,
  EyeIcon,
  FileArrowDownIcon,
  FileArrowUpIcon,
  FolderIcon,
  FoldersIcon,
  PencilSimpleIcon,
  TrashSimpleIcon,
} from "@phosphor-icons/react";
import { Directory } from "../../models/Directory";
import { TreeRow } from "../../models/global/Types";
import { useProfile } from "../../stores/useProfile";
import { useProject } from "../../stores/useProject";

interface ContextMenuProps {
  row: TreeRow;
  onOpen: () => void;
  onChange: () => void;
  onRename: () => void;
  onUpload?: () => void;
  onDelete?: () => void;
  onDownload?: () => void;
  onDownloadZip?: () => void;
}

export const ContextMenu = ({
  row,
  onOpen,
  onChange,

  onRename,
  onDelete,
  onDownload,
  onDownloadZip,
}: ContextMenuProps) => {
  const [confirmation, setConfirmation] = useState<boolean>(false);
  const { user } = useProfile();
  const { addDirectory, removeDirectory, addFile } = useProject();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    setConfirmation(false);
  };

  const enabled = useMemo(() => {
    const isDirectory = row.type === "directory";
    const directory = isDirectory ? row.data : null;
    const file = !isDirectory ? row.data : null;

    return {
      open: !isDirectory,
      upload: isDirectory,
      addImage: isDirectory,
      newFolder: isDirectory,
      rename: true,
      move: false,
      downloadZip: isDirectory,
      download: !isDirectory,
      delete: !isDirectory || row.data.name !== "root",
    };
  }, [row]);

  const onNewFolder = async () => {
    if (user) {
      await addDirectory(user, row.data.id, "Nieuwe folder");
      onChange();
    }
  };

  const onRemove = async () => {
    if (!user) return;
    await removeDirectory(user, row.data!.id);
    onChange();
  };

  const onUpload = async () => {};

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    console.log("CHANGE FIRED", files);

    if (!files || !user) return;

    for (const file of Array.from(files)) {
      await addFile(user, row.data.id, file);
    }

    event.target.value = "";
    onChange();
  };

  return (
    <>
      <StyledContextMenu>
        {enabled.open && (
          <MenuItem onClick={onOpen} className="menuItem">
            <EyeIcon size={18} weight="bold" />
            <Typography variant="body1">Openen</Typography>
          </MenuItem>
        )}

        {enabled.upload && (
          <MenuItem
            className="menuItem"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}>
            <FileArrowUpIcon size={18} weight="bold" />
            <Typography variant="body1">File Uploaden</Typography>
          </MenuItem>
        )}

        {enabled.addImage && (
          <MenuItem onClick={onUpload} className="menuItem">
            <CameraPlusIcon size={18} weight="bold" />
            <Typography variant="body1">Foto maken</Typography>
          </MenuItem>
        )}

        {enabled.newFolder && (
          <MenuItem onClick={onNewFolder} className="menuItem">
            <FolderIcon size={18} weight="bold" />
            <Typography variant="body1">Nieuwe Folder</Typography>
          </MenuItem>
        )}

        {enabled.rename && (
          <MenuItem onClick={onRename} className="menuItem">
            <PencilSimpleIcon size={18} weight="bold" />
            <Typography variant="body1">Naam veranderen</Typography>
          </MenuItem>
        )}

        {enabled.download && (
          <MenuItem onClick={onDownload} className="menuItem">
            <FileArrowDownIcon size={18} weight="bold" />
            <Typography variant="body1">Download</Typography>
          </MenuItem>
        )}

        {enabled.downloadZip && (
          <MenuItem onClick={onDownloadZip} className="menuItem">
            <DownloadSimpleIcon size={18} weight="bold" />
            <Typography variant="body1">Download Zip</Typography>
          </MenuItem>
        )}

        {enabled.move && (
          <MenuItem onClick={onDownloadZip} className="menuItem">
            <FoldersIcon size={18} weight="bold" />
            <Typography variant="body1">Verplaatsen naar...</Typography>
          </MenuItem>
        )}

        {enabled.delete &&
          (confirmation ? (
            <MenuItem onClick={onRemove} className="menuItem delete">
              <CheckIcon size={24} weight="bold" />
              <Typography variant="body1">Verwijderen</Typography>
            </MenuItem>
          ) : (
            <MenuItem
              onClick={() => setConfirmation(true)}
              className="menuItem delete">
              <TrashSimpleIcon size={18} weight="bold" />
              <Typography variant="body1">Verwijderen</Typography>
            </MenuItem>
          ))}
      </StyledContextMenu>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </>
  );
};
